// services/v3/datasource/fixtures/mock-store.ts — deterministic test data (SPEC §4.4)
//
// Everything here is shaped like the RAW RTDB payloads the POS writes, so the
// same `normalize*` helpers used by RtdbSource can be applied to it:
//   - `settings` is an ARRAY of { settingId, value } records (including 'name',
//     'tables' zones, 'channel_price' and the QR allowlist 'qr_all').
//   - `products` is `{ list, computed }`; option groups live in `list` as
//     ordinary products carrying `type` / `limit` / `max` / `items`, and menu
//     items point at them through `options: { [key]: optionGroupProductId }`
//     (legacy `useCartItemOptionsStore.initOptions` does `_.values(options)`
//     and looks each id up in the product list).
// Nothing is frozen: MockSource hands out structured clones.
import type { QrToken } from '~/types/v3/token'

/** Raw (pre-normalisation) settings record, exactly like RTDB. */
export interface RawSettingRecord { settingId: string; value: unknown }

/** Raw `stores/{id}/products` node. */
export interface RawProductsNode {
  list: Array<Record<string, unknown>>
  computed: Array<Record<string, unknown>>
}

export interface MockStoreFixture {
  storeId: string
  /** what `getStoreProfile()` returns (the HTTP store API in RtdbSource) */
  profileName: string
  settings: RawSettingRecord[]
  products: RawProductsNode
  soldout: Record<string, unknown>
  /** keyed by table name */
  tableState: Record<string, Record<string, unknown>>
  /** keyed by table name */
  tableItems: Record<string, { items: Array<Record<string, unknown>> }>
  versions: { settings: string; products: string; channel_price: string; menuSet: string }
}

// ---------------------------------------------------------------------------
// Store S1 — "Mock Restaurant"
// ---------------------------------------------------------------------------

// --- option groups (products with a `type`) --------------------------------
const S1_OPTION_GROUPS: Array<Record<string, unknown>> = [
  {
    productId: 's1-opt-spice',
    name: 'ระดับความเผ็ด',
    type: 'single',
    level: 2,
    price: 0,
    items: [
      { productId: 's1-opt-spice-0', name: 'ไม่เผ็ด', price: 0 },
      { productId: 's1-opt-spice-1', name: 'เผ็ดน้อย', price: 0 },
      { productId: 's1-opt-spice-2', name: 'เผ็ดมาก', price: 0 },
    ],
  },
  {
    productId: 's1-opt-topping',
    name: 'ท็อปปิ้ง',
    type: 'multiple',
    // NOTE: strings on purpose — the POS stores limits as text (SPEC §6 parseInt)
    limit: '2',
    level: 2,
    price: 0,
    items: [
      { productId: 's1-opt-topping-egg', name: 'ไข่ดาว', price: 10 },
      { productId: 's1-opt-topping-pork', name: 'หมูเพิ่ม', price: 20 },
      { productId: 's1-opt-topping-veg', name: 'ผักเพิ่ม', price: 10 },
    ],
  },
  {
    productId: 's1-opt-side',
    name: 'ของเคียง',
    type: 'qty',
    limit: 3,
    level: 2,
    price: 0,
    items: [
      { productId: 's1-opt-side-sauce', name: 'น้ำจิ้ม', price: 5 },
      { productId: 's1-opt-side-soup', name: 'ซุป', price: 10 },
    ],
  },
  {
    productId: 's1-opt-mixed',
    name: 'เครื่องเคียงรวม',
    type: 'mixed',
    max: 3,
    level: 2,
    price: 0,
    items: [
      { productId: 's1-opt-mixed-egg', name: 'ไข่ต้ม', price: 10, max: 2 },
      { productId: 's1-opt-mixed-sausage', name: 'ไส้กรอก', price: 15, max: 2 },
    ],
  },
]

// --- menu items ------------------------------------------------------------
const S1_ITEMS: Array<Record<string, unknown>> = [
  // group 1 — อาหารจานเดียว
  {
    productId: 's1-p101',
    name: 'ข้าวผัดกุ้ง',
    name2: 'Shrimp Fried Rice',
    code: 'FR01',
    price: 80,
    row: 1,
    level: 1,
    parentId: 's1-g1',
    description: 'ข้าวผัดกุ้งสด',
    options: { '0': 's1-opt-spice' },
  },
  {
    productId: 's1-p102',
    name: 'ผัดไทยกุ้งสด',
    name2: 'Pad Thai',
    code: 'PT01',
    price: 70,
    row: 2,
    level: 1,
    parentId: 's1-g1',
    options: { '0': 's1-opt-topping' },
  },
  {
    productId: 's1-p103',
    name: 'ข้าวมันไก่',
    name2: 'Hainanese Chicken Rice',
    code: 'CR01',
    price: 60,
    row: 3,
    level: 1,
    parentId: 's1-g1',
    options: { '0': 's1-opt-side' },
  },
  {
    productId: 's1-p104',
    name: 'กะเพราหมูสับ',
    name2: 'Basil Pork',
    code: 'BP01',
    price: 65,
    row: 4,
    level: 1,
    parentId: 's1-g1',
    options: { '0': 's1-opt-mixed' },
  },
  {
    // sold out (see `soldout` below)
    productId: 's1-p105',
    name: 'ข้าวหมูแดง',
    name2: 'Red Pork Rice',
    code: 'RP01',
    price: 65,
    row: 5,
    level: 1,
    parentId: 's1-g1',
  },
  // group 2 — เครื่องดื่ม
  { productId: 's1-p201', name: 'ชาเย็น', name2: 'Thai Iced Tea', code: 'DR01', price: 45, row: 1, level: 1, parentId: 's1-g2' },
  { productId: 's1-p202', name: 'กาแฟเย็น', name2: 'Iced Coffee', code: 'DR02', price: 50, row: 2, level: 1, parentId: 's1-g2' },
  { productId: 's1-p203', name: 'น้ำเปล่า', name2: 'Water', code: 'DR03', price: 15, row: 3, level: 1, parentId: 's1-g2' },
  { productId: 's1-p204', name: 'โค้ก', name2: 'Coke', code: 'DR04', price: 25, row: 4, level: 1, parentId: 's1-g2' },
  // group 3 — ของหวาน
  { productId: 's1-p301', name: 'ไอศกรีมกะทิ', name2: 'Coconut Ice Cream', code: 'DS01', price: 40, row: 1, level: 1, parentId: 's1-g3' },
  { productId: 's1-p302', name: 'ข้าวเหนียวมะม่วง', name2: 'Mango Sticky Rice', code: 'DS02', price: 90, row: 2, level: 1, parentId: 's1-g3' },
  { productId: 's1-p303', name: 'บัวลอย', name2: 'Bua Loy', code: 'DS03', price: 45, row: 3, level: 1, parentId: 's1-g3' },
]

const itemsOf = (parentId: string) => S1_ITEMS.filter((p) => p.parentId === parentId)

const S1_GROUPS: Array<Record<string, unknown>> = [
  { productId: 's1-g1', name: 'อาหารจานเดียว', row: 1, level: 0, items: itemsOf('s1-g1') },
  { productId: 's1-g2', name: 'เครื่องดื่ม', row: 2, level: 0, items: itemsOf('s1-g2') },
  { productId: 's1-g3', name: 'ของหวาน', row: 3, level: 0, items: itemsOf('s1-g3') },
]

/** `qr_all` allows the three groups outright => every item is on the menu. */
const S1_QR_ALL: Record<string, true> = {
  's1-g1': true,
  's1-g2': true,
  's1-g3': true,
}

const S1_SETTINGS: RawSettingRecord[] = [
  { settingId: 'name', value: 'Mock Restaurant' },
  { settingId: 'tables', value: [{ name: 'A', value: 5 }, { name: 'B', value: 3 }] },
  {
    settingId: 'channel_price',
    value: {
      member: {
        's1-p101': 72,
        's1-p102': 63,
        's1-p201': 40,
        // strings are coerced by normalize.pickChannelPrice
        's1-p302': '80',
      },
    },
  },
  { settingId: 'qr_all', value: S1_QR_ALL },
  { settingId: 'vat', value: 7 },
]

/** One already-served line so `/v3/summary` has something to total up. */
const s1TableItems = (): Array<Record<string, unknown>> => ([
  {
    productId: 's1-p201',
    name: 'ชาเย็น',
    qty: 2,
    price: 45,
    extendedPrice: [90, 90, 90],
    dc1: [0, 0, 0],
    sc: [0, 0, 0],
    dc2: [0, 0, 0],
    tax: [5.89, 5.89, 5.89],
    rounding: [0, 0, 0],
    amount: [90, 90, 90],
    comments: null,
    timestamp: '2024-01-01T10:00:00.000Z',
  },
  {
    productId: 's1-p101',
    name: 'ข้าวผัดกุ้ง',
    qty: 1,
    price: 80,
    extendedPrice: [80, 80, 80],
    dc1: [0, 0, 0],
    sc: [0, 0, 0],
    dc2: [0, 0, 0],
    tax: [5.23, 5.23, 5.23],
    rounding: [0, 0, 0],
    amount: [80, 80, 80],
    options2: [{ productId: 's1-opt-spice-1', name: 'เผ็ดน้อย', price: 0, qty: 1 }],
    comments: null,
    timestamp: '2024-01-01T10:01:00.000Z',
  },
])

export const MOCK_STORE_S1: MockStoreFixture = {
  storeId: 'S1',
  profileName: 'Mock Restaurant',
  settings: S1_SETTINGS,
  products: { list: [...S1_ITEMS, ...S1_OPTION_GROUPS], computed: S1_GROUPS },
  soldout: { 's1-p105': true },
  tableState: {
    A1: { lock: false, guests: 2, channel: { channelId: 'member' }, startDate: '2024-01-01T10:00:00.000Z', subTotal: 170, grandTotal: 170 },
    A3: { lock: false, guests: 1, channel: null, startDate: '2024-01-01T10:00:00.000Z' },
    B2: { lock: false, guests: 4, channel: null, startDate: '2024-01-01T10:00:00.000Z' },
  },
  tableItems: {
    A1: { items: s1TableItems() },
    A3: { items: [] },
    B2: { items: [] },
  },
  versions: {
    settings: 'mock-s1-settings-1',
    products: 'mock-s1-products-1',
    channel_price: 'mock-s1-settings-1',
    menuSet: 'mock-s1-settings-1',
  },
}

// ---------------------------------------------------------------------------
// Store S2 — "Second Cafe" (different names/prices, 2 groups)
// ---------------------------------------------------------------------------

const S2_OPTION_GROUPS: Array<Record<string, unknown>> = [
  {
    productId: 's2-opt-sugar',
    name: 'ระดับความหวาน',
    type: 'single',
    level: 2,
    price: 0,
    items: [
      { productId: 's2-opt-sugar-0', name: 'ไม่หวาน', price: 0 },
      { productId: 's2-opt-sugar-1', name: 'หวานน้อย', price: 0 },
      { productId: 's2-opt-sugar-2', name: 'หวานปกติ', price: 0 },
    ],
  },
]

const S2_ITEMS: Array<Record<string, unknown>> = [
  { productId: 's2-p101', name: 'อเมริกาโน่', name2: 'Americano', code: 'C01', price: 65, row: 1, level: 1, parentId: 's2-g1', options: { '0': 's2-opt-sugar' } },
  { productId: 's2-p102', name: 'ลาเต้', name2: 'Latte', code: 'C02', price: 75, row: 2, level: 1, parentId: 's2-g1', options: { '0': 's2-opt-sugar' } },
  { productId: 's2-p103', name: 'มัทฉะลาเต้', name2: 'Matcha Latte', code: 'C03', price: 85, row: 3, level: 1, parentId: 's2-g1' },
  { productId: 's2-p201', name: 'ครัวซองต์', name2: 'Croissant', code: 'B01', price: 55, row: 1, level: 1, parentId: 's2-g2' },
  { productId: 's2-p202', name: 'ชีสเค้ก', name2: 'Cheesecake', code: 'B02', price: 120, row: 2, level: 1, parentId: 's2-g2' },
]

const s2ItemsOf = (parentId: string) => S2_ITEMS.filter((p) => p.parentId === parentId)

const S2_GROUPS: Array<Record<string, unknown>> = [
  { productId: 's2-g1', name: 'กาแฟ', row: 1, level: 0, items: s2ItemsOf('s2-g1') },
  { productId: 's2-g2', name: 'เบเกอรี่', row: 2, level: 0, items: s2ItemsOf('s2-g2') },
]

export const MOCK_STORE_S2: MockStoreFixture = {
  storeId: 'S2',
  profileName: 'Second Cafe',
  settings: [
    { settingId: 'name', value: 'Second Cafe' },
    { settingId: 'tables', value: [{ name: 'A', value: 4 }, { name: 'B', value: 2 }] },
    { settingId: 'channel_price', value: { member: { 's2-p101': 55, 's2-p102': 65 } } },
    { settingId: 'qr_all', value: { 's2-g1': true, 's2-g2': true } },
  ],
  products: { list: [...S2_ITEMS, ...S2_OPTION_GROUPS], computed: S2_GROUPS },
  soldout: {},
  tableState: {
    A1: { lock: false, guests: 1, channel: null, startDate: '2024-01-01T10:00:00.000Z' },
  },
  tableItems: { A1: { items: [] } },
  versions: {
    settings: 'mock-s2-settings-1',
    products: 'mock-s2-products-1',
    channel_price: 'mock-s2-settings-1',
    menuSet: 'mock-s2-settings-1',
  },
}

export const MOCK_STORES: Record<string, MockStoreFixture> = {
  S1: MOCK_STORE_S1,
  S2: MOCK_STORE_S2,
}

// ---------------------------------------------------------------------------
// Tokens (docId === qrcodeId)
// ---------------------------------------------------------------------------

const minutesAgoISO = (m: number): string => new Date(Date.now() - m * 60_000).toISOString()

/**
 * Built fresh on every call so relative `createdAt` values ('mock-timed',
 * 'mock-expired') stay relative to *now* for the whole life of the page.
 */
export function buildMockTokens(): Record<string, QrToken> {
  const nowISO = new Date().toISOString()
  return {
    'mock-guest': {
      qrcodeId: 'mock-guest',
      type: 'guest',
      storeId: 'S1',
      table: 'A1',
      settingId: 'qr_all',
      createdAt: nowISO,
      length: 0, // unlimited
      hash: 'mock-hash-guest',
    },
    'mock-guest-b': {
      qrcodeId: 'mock-guest-b',
      type: 'guest',
      storeId: 'S1',
      table: 'B2',
      settingId: 'qr_all',
      createdAt: nowISO,
      length: 0,
      hash: 'mock-hash-guest-b',
    },
    'mock-store2': {
      qrcodeId: 'mock-store2',
      type: 'guest',
      storeId: 'S2',
      table: 'A1',
      settingId: 'qr_all',
      createdAt: nowISO,
      length: 0,
      hash: 'mock-hash-store2',
    },
    'mock-timed': {
      qrcodeId: 'mock-timed',
      type: 'guest',
      storeId: 'S1',
      table: 'A1',
      settingId: 'qr_all',
      createdAt: minutesAgoISO(10),
      length: 60,
      hash: 'mock-hash-timed',
    },
    'mock-expired': {
      qrcodeId: 'mock-expired',
      type: 'guest',
      storeId: 'S1',
      table: 'A1',
      settingId: 'qr_all',
      createdAt: minutesAgoISO(60),
      length: 1,
      hash: 'mock-hash-expired',
    },
    'mock-employee': {
      qrcodeId: 'mock-employee',
      type: 'employee',
      storeId: 'S1',
      table: 'A1',
      settingId: 'qr_all',
      createdAt: nowISO,
      length: 0,
      pin: '1234',
      hash: 'mock-hash-employee',
    },
    // A document that does NOT repeat its own id in a `qrcodeId` field. Real
    // data looks like this (the id lives in the path, not in the document), and
    // it is what broke legacy: `store-legacy/auth.ts` saved `.data()` and then
    // read `token.qrcodeId` back as undefined. Every V3 path must survive it.
    'mock-no-id': {
      type: 'guest',
      storeId: 'S1',
      table: 'A1',
      settingId: 'qr_all',
      createdAt: nowISO,
      length: 0,
      hash: 'mock-hash-no-id',
    } as QrToken,

    // watchQrcode() emits null 2 s after subscribe => "table paid"
    'mock-paid': {
      qrcodeId: 'mock-paid',
      type: 'guest',
      storeId: 'S1',
      table: 'A3',
      settingId: 'qr_all',
      createdAt: nowISO,
      length: 0,
      hash: 'mock-hash-paid',
    },
  }
}

/** qrcodeIds whose `watchQrcode` deletes the doc shortly after subscribing. */
export const MOCK_PAID_QRCODE_IDS: readonly string[] = ['mock-paid']

/** Delay (ms) before 'mock-paid' emits `null`. */
export const MOCK_PAID_DELAY_MS = 2000
